<template>
  <div class="maintenance-tab p-3">
    <!-- Visibility Marker for Debugging -->
    <div style="display:none">DEBUG: items={{ items ? items.length : 'null' }}, loading={{ loading }}</div>

    <!-- Item Brief Section (Consolidated Parts Usage) -->
    <b-row v-if="selectedVehicleId && (itemBrief || []).length > 0" class="mb-4">
      <b-col lg="12">
        <b-card no-body class="border-0 shadow-sm glass-card mb-0">
          <div class="p-3 bg-primary border-bottom d-flex justify-content-between align-items-center">
            <div class="d-flex align-items-center">
              <md-icon class="text-white mr-2">analytics</md-icon>
              <h5 class="text-white font-weight-bold mb-0">Consumed Parts Summary (Total Brief)</h5>
            </div>
            <b-badge pill variant="light" class="px-3 py-1">Deep Item Brief</b-badge>
          </div>
          <div class="p-0 position-relative">
            <div v-if="briefLoading" class="brief-overlay">
              <b-spinner variant="primary"></b-spinner>
            </div>
            <b-table
              small
              hover
              striped
              responsive
              :items="itemBrief"
              :fields="briefFields"
              class="brief-table mb-0"
            >
              <template v-slot:cell(totalSpend)="scope">
                <span class="font-weight-bold text-primary">₹ {{ Number(scope.value).toLocaleString('en-IN') }}</span>
              </template>
              <template v-slot:cell(priceRange)="scope">
                 <small class="text-muted">₹{{ scope.item.minRate }} - ₹{{ scope.item.maxRate }}</small>
              </template>
            </b-table>
          </div>
        </b-card>
      </b-col>
    </b-row>

    <!-- Detailed History Section -->
    <b-card no-body class="border-0 shadow-sm overflow-hidden glass-card">
      <div class="p-4 bg-white border-bottom d-flex justify-content-between align-items-center">
        <div>
          <h4 class="font-weight-bold mb-1 text-dark">Maintenance History</h4>
          <p class="text-muted small mb-0">Expand rows to view part-level itemized breakdown</p>
        </div>
        <b-button variant="danger" class="btn-book px-4 shadow-sm" @click="$emit('book-service')">
          <md-icon size="20" class="mr-2">add</md-icon> BOOK SERVICE
        </b-button>
      </div>

      <div class="table-responsive">
        <b-table
          :items="items"
          :fields="historyFields"
          hover
          responsive
          class="maintenance-table mb-0"
          :busy="loading"
          head-variant="light"
          @row-clicked="toggleDetails"
        >
          <template slot="table-busy">
            <div class="text-center text-primary my-5">
              <b-spinner class="align-middle"></b-spinner>
              <strong class="ml-2">Loading history...</strong>
            </div>
          </template>

          <template slot="cell(ticketId)" slot-scope="scope">
            <div class="d-flex align-items-center">
              <md-icon size="16" class="mr-2 text-muted">{{ scope && scope.item && scope.item._showDetails ? 'keyboard_arrow_down' : 'keyboard_arrow_right' }}</md-icon>
              <span class="text-dark font-weight-bold">{{ scope ? scope.value : '' }}</span>
            </div>
          </template>

          <template slot="cell(vehicleNo)" slot-scope="scope">
            <a href="javascript:void(0)" @click.stop="$emit('select-vehicle', scope && scope.item ? scope.item.vehicleNo : '')" class="font-weight-bold text-primary vehicle-link">
              {{ scope ? scope.value : '' }}
            </a>
          </template>

          <template slot="cell(serviceDate)" slot-scope="scope">
            {{ formatDisplayDate(scope ? scope.value : null) }}
          </template>

          <template slot="cell(status)" slot-scope="scope">
            <b-badge 
              pill 
              :class="getStatusClass(scope ? scope.value : '')"
              class="status-pill"
            >
              {{ (scope ? scope.value : '') || 'Pending' }}
            </b-badge>
          </template>

          <template slot="cell(estimatedCost)" slot-scope="scope">
            <span class="font-weight-bold text-dark">₹ {{ Number((scope ? scope.value : 0) || 0).toLocaleString('en-IN') }}</span>
          </template>

          <!-- Row Expansion Details Slots -->
          <template slot="row-details" slot-scope="scope">
            <b-card class="m-2 item-details-card border-0 shadow-sm bg-light">
              <h6 class="text-primary font-weight-bold border-bottom pb-2 mb-3">
                 <md-icon size="18" class="mr-1">list_alt</md-icon> Itemized Breakdown for {{ scope && scope.item ? scope.item.ticketId : '' }}
              </h6>
              <b-table
                striped
                hover
                small
                head-variant="dark"
                :items="(scope && scope.item && scope.item.items) || []"
                :fields="itemizedFields"
                class="item-details-table mb-0"
              >
                <template slot="cell(ratePerUnit)" slot-scope="innerRow">
                  ₹ {{ Number(String(innerRow.value).replace(/,/g, '')).toLocaleString('en-IN') }}
                </template>
                <template slot="cell(totalAmount)" slot-scope="innerRow">
                  <span class="font-weight-bold">₹ {{ Number(String(innerRow.value).replace(/,/g, '')).toLocaleString('en-IN') }}</span>
                </template>
              </b-table>
            </b-card>
          </template>

          <template slot="cell(actions)" slot-scope="scope">
            <div class="d-flex">
               <b-button 
                size="sm" 
                variant="light" 
                class="btn-action shadow-sm mr-2"
                @click.stop="toggleDetails(scope ? scope.item : null)"
              >
                <md-icon size="18" class="text-primary">{{ scope && scope.item && scope.item._showDetails ? 'visibility_off' : 'visibility' }}</md-icon>
              </b-button>
              <b-button 
                size="sm" 
                variant="light" 
                class="btn-action shadow-sm"
                @click.stop="$emit('show-service-detail', scope ? scope.item : null)"
              >
                <md-icon size="18" class="text-secondary">open_in_new</md-icon>
              </b-button>
            </div>
          </template>
        </b-table>
      </div>

      <div v-if="(!items || items.length === 0) && !loading" class="text-center p-5">
        <md-icon size="48" class="text-muted mb-3">history</md-icon>
        <h5 class="text-muted">No maintenance records found for this period.</h5>
      </div>
    </b-card>
  </div>
</template>

<script>
import moment from 'moment';

export default {
  name: 'MaintenanceHealthTab',
  props: {
    items: { type: Array, required: true },
    itemBrief: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
    briefLoading: { type: Boolean, default: false },
    selectedVehicleId: { type: [Number, String], default: null }
  },
  data() {
    return {
      historyFields: [
        { key: 'ticketId', label: 'Ticket ID', sortable: true },
        { key: 'vehicleNo', label: 'Vehicle No', sortable: true },
        { key: 'serviceDate', label: 'Service Date', sortable: true },
        { key: 'serviceType', label: 'Service Type', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'estimatedCost', label: 'Est. Cost', sortable: true, tdClass: 'text-right', thClass: 'text-right' },
        { key: 'actions', label: 'Action', tdClass: 'text-center', thClass: 'text-center' }
      ],
      itemizedFields: [
        { key: 'itemName', label: 'Item Description' },
        { key: 'itemQuantity', label: 'Qty', tdClass: 'text-center' },
        { key: 'ratePerUnit', label: 'Rate', tdClass: 'text-right' },
        { key: 'totalAmount', label: 'Amount', tdClass: 'text-right' }
      ],
      briefFields: [
        { key: 'itemName', label: 'Item Name', sortable: true },
        { key: 'totalQuantity', label: 'Total Qty Consumed', sortable: true, tdClass: 'text-center' },
        { key: 'priceRange', label: 'Avg Price Range', tdClass: 'text-center' },
        { key: 'totalSpend', label: 'Cumulative Spend', sortable: true, tdClass: 'text-right' }
      ]
    };
  },
  methods: {
    toggleDetails(row) {
      if (!row || typeof row !== 'object') return;
      this.$set(row, '_showDetails', !row._showDetails);
    },
    formatDisplayDate(date) {
      if (!date) return 'N/A';
      return moment(date).format('YYYY-MM-DD');
    },
    getStatusClass(status) {
      const s = String(status || '').toLowerCase();
      if (s.includes('approve') || s.includes('complete')) return 'badge-success';
      if (s.includes('reject')) return 'badge-danger';
      return 'badge-warning';
    }
  }
};
</script>

<style scoped>
.maintenance-tab {
  background: #f8fafc;
  min-height: 500px;
}
.glass-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 12px;
}
.maintenance-table {
  font-size: 13px;
}
.maintenance-table thead th {
  background: #f1f5f9;
  color: #475569;
  text-transform: uppercase;
  font-size: 11px;
  letter-spacing: 0.5px;
  border-top: none;
}
.vehicle-link {
  color: #2563eb;
  text-decoration: none;
}
.vehicle-link:hover {
  text-decoration: underline !important;
}
.status-pill {
  padding: 6px 12px; 
  font-weight: 600; 
  font-size: 10px; 
  text-transform: uppercase;
}
.item-details-card {
  border-radius: 10px;
}
.item-details-table {
  font-size: 12px;
}
.brief-table {
  font-size: 12px;
}
.brief-table thead th {
  background: #1e293b !important;
  color: white !important;
  font-size: 10px;
  border: none;
}
.btn-book {
  border-radius: 8px;
  font-weight: 700;
  font-size: 12px;
  letter-spacing: 0.5px;
}
.btn-action {
  border-radius: 6px;
  padding: 4px;
}
.badge-success { background-color: #dcfce7; color: #166534; }
.badge-warning { background-color: #fef9c3; color: #854d0e; }
.badge-danger { background-color: #fee2e2; color: #991b1b; }

.brief-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(255,255,255,0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10;
}
</style>



// WEBPACK FOOTER //
// MaintenanceHealthTab.vue?25b7f0de